import type { DirectoryHandle } from "idfweb-project";

/**
 * The projects this browser keeps, in IndexedDB, one record per project under
 * its id, the id in its URL. A project is kept in one of two places: in the
 * browser, as files in the origin private file system under the id (see
 * `saved-tree.ts`), or in a folder on the user's computer, whose handle the
 * record holds. A handle survives structured cloning; permission over it does
 * not survive the visit, so a folder is reopened after asking.
 *
 * Storage can be refused, so reading answers nothing; a write that fails
 * rejects.
 */

export interface ProjectRecord {
    id: string;
    /** The name the list shows, which the user can change. */
    name: string;
    kind: "browser" | "folder";
    /** The tree's directory in `/project`, and in the browser's storage under the id. */
    tree: string;
    /** The folder, for a project kept in one. */
    handle?: DirectoryHandle;
    /** Milliseconds since the epoch. */
    opened: number;
    /** The example link of the example it was copied from, as `exampleLink` writes it. */
    source?: string;
}

const DATABASE = "idfweb-projects";
const STORE = "projects";

/** The kept projects, the one opened last first. */
export async function projectRecords(): Promise<ProjectRecord[]> {
    try {
        const all = (await inStore("readonly", (store) => store.getAll())) as ProjectRecord[];
        return all.sort((a, b) => b.opened - a.opened);
    } catch (e) {
        console.warn("idfweb: the kept projects cannot be read", e);
        return [];
    }
}

export async function projectRecord(id: string): Promise<ProjectRecord | undefined> {
    try {
        return (await inStore("readonly", (store) => store.get(id))) as ProjectRecord | undefined;
    } catch (e) {
        console.warn("idfweb: the kept projects cannot be read", e);
        return undefined;
    }
}

/** Add or replace a record. */
export async function putProjectRecord(record: ProjectRecord): Promise<void> {
    await inStore("readwrite", (store) => store.put(record, record.id));
}

export async function deleteProjectRecord(id: string): Promise<void> {
    await inStore("readwrite", (store) => store.delete(id));
}

/** The record of the folder `handle` points to, if one is kept: a folder opened again keeps its id. */
export async function folderRecord(handle: DirectoryHandle): Promise<ProjectRecord | undefined> {
    for (const record of await projectRecords()) {
        if (record.handle !== undefined && (await isSame(record.handle, handle))) return record;
    }
    return undefined;
}

async function isSame(one: DirectoryHandle, other: DirectoryHandle): Promise<boolean> {
    try {
        return (await one.isSameEntry?.(other)) ?? one.name === other.name;
    } catch {
        return false;
    }
}

async function inStore(mode: IDBTransactionMode, request: (store: IDBObjectStore) => IDBRequest): Promise<unknown> {
    const db = await opened();
    return await new Promise<unknown>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const result = request(transaction.objectStore(STORE));
        transaction.oncomplete = () => resolve(result.result);
        transaction.onabort = transaction.onerror = () =>
            reject(transaction.error ?? new Error("the transaction failed"));
    });
}

let database: Promise<IDBDatabase> | undefined;

/** The database, opened once per page; its first opening in this browser takes in the earlier stores. */
function opened(): Promise<IDBDatabase> {
    database ??= open(DATABASE, (db) => db.createObjectStore(STORE)).then(async ({ db, created }) => {
        db.onversionchange = () => {
            db.close();
            database = undefined;
        };
        if (created)
            await migrate(db).catch((e: unknown) => console.warn("idfweb: earlier projects were not taken in", e));
        return db;
    });
    database.catch(() => (database = undefined));
    return database;
}

/**
 * Take in the projects an earlier page kept: those saved in the browser, under
 * the key their files are stored under, and the folders it kept in sync. A
 * folder it only copied was never kept and is left out.
 */
async function migrate(db: IDBDatabase): Promise<void> {
    const saved = (await readOld("idfweb-saved", "projects", undefined)) as
        { key: string; name: string; tree: string; opened: number }[] | undefined;
    const recent = (await readOld("idfweb", "recent", "directories")) as
        ({ handle: DirectoryHandle; sync: boolean } | DirectoryHandle)[] | undefined;
    const records: ProjectRecord[] = (saved ?? []).map((project) => ({
        id: project.key,
        name: project.name,
        kind: "browser",
        tree: project.tree,
        opened: project.opened,
    }));
    // Newest first, and older than any saved project.
    (recent ?? []).forEach((item, i) => {
        const { handle, sync } = "handle" in item ? item : { handle: item, sync: true };
        if (!sync) return;
        records.push({
            id: crypto.randomUUID(),
            name: handle.name,
            kind: "folder",
            tree: handle.name,
            handle,
            opened: -i,
        });
    });
    if (records.length > 0) {
        await new Promise<void>((resolve, reject) => {
            const transaction = db.transaction(STORE, "readwrite");
            for (const record of records) transaction.objectStore(STORE).put(record, record.id);
            transaction.oncomplete = () => resolve();
            transaction.onabort = transaction.onerror = () => reject(transaction.error);
        });
    }
    indexedDB.deleteDatabase("idfweb-saved");
    indexedDB.deleteDatabase("idfweb");
}

/** Everything in an earlier database's store, or the value under `key`; undefined when there is no such database. */
async function readOld(name: string, store: string, key: string | undefined): Promise<unknown> {
    let db: IDBDatabase;
    try {
        ({ db } = await open(name, undefined));
    } catch {
        return undefined;
    }
    try {
        return await new Promise<unknown>((resolve, reject) => {
            const request = db.transaction(store, "readonly").objectStore(store);
            const result = key === undefined ? request.getAll() : request.get(key);
            result.onsuccess = () => resolve(result.result);
            result.onerror = () => reject(result.error);
        });
    } catch {
        return undefined;
    } finally {
        db.close();
    }
}

/**
 * Open the database `name` at version 1. Without `create`, one that does not
 * exist yet is not created: the upgrade is aborted, and the open rejects.
 */
function open(
    name: string,
    create: ((db: IDBDatabase) => void) | undefined,
): Promise<{ db: IDBDatabase; created: boolean }> {
    return new Promise((resolve, reject) => {
        let created = false;
        const request = indexedDB.open(name, 1);
        request.onupgradeneeded = () => {
            if (create === undefined) request.transaction?.abort();
            else {
                create(request.result);
                created = true;
            }
        };
        request.onsuccess = () => resolve({ db: request.result, created });
        request.onerror = () => reject(request.error ?? new Error("the database did not open"));
        request.onblocked = () => reject(new Error("the database is blocked by another tab"));
    });
}
